import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuArrowLeft,
  LuPlus,
  LuSearch,
  LuImageOff,
  LuChevronRight,
} from "react-icons/lu";
import { AdminInput, AdminTextarea } from "../../../../../components/adminForm";
import "../../products/edition/productEditorPage.css";
import { EditorDialog } from "../../products/edition/editorDialog";
import {
  getCatalogs,
  createCatalog,
  updateCatalog,
  updateCatalogProducts,
} from "../../../../../api/catalogs";
import { getProductsWithOrganization } from "../../../../../api/products";
import { getCategories } from "../../../../../api/categories";
import {
  updateFloatPanel,
  requestTableRefresh,
} from "../../../../../reducers/userConfigSlice";
import {
  productMoney,
  productPriceWithTax,
} from "../../products/productListModel";
import {
  normalizeSearch,
  activeValue,
} from "../../../../../components/collectionTools";

const memberIds = (row) => (row?.Products || []).map((p) => p.productId);
const equalIds = (a, b) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
const draft = (row) => ({
  name: row?.name || "",
  description: row?.description || "",
  isActive: row ? activeValue(row.isActive) : true,
});
export const CatalogEditor = () => {
  const panel = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel,
  );
  const dispatch = useDispatch();
  const [id, setId] = useState(panel.isEmpty ? null : panel.queryId);
  const [row, setRow] = useState(null),
    [form, setForm] = useState(null),
    [baseline, setBaseline] = useState(null);
  const [members, setMembers] = useState([]),
    [originalMembers, setOriginalMembers] = useState([]);
  const [available, setAvailable] = useState(null),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const [search, setSearch] = useState(""),
    [selecting, setSelecting] = useState(false),
    [selected, setSelected] = useState([]);
  const [picker, setPicker] = useState(null),
    [pickerSearch, setPickerSearch] = useState(""),
    [picks, setPicks] = useState([]),
    [discard, setDiscard] = useState(false);
  const lock = useRef(false),
    editor = useRef(null);
  const dirty =
    form &&
    (JSON.stringify(form) !== JSON.stringify(baseline) ||
      !equalIds(members, originalMembers));
  const close = () => {
    if (lock.current) return;
    if (dirty) {
      setDiscard(true);
      return;
    }
    dispatch(
      updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
    );
  };
  useEffect(() => {
    let current = true;
    setError("");
    Promise.all([getCatalogs(), getProductsWithOrganization(), getCategories()])
      .then((responses) => {
        if (!current) return;
        if (responses.some((r) => r?.error !== false || !Array.isArray(r.data)))
          throw Error();
        const loaded = panel.isEmpty
          ? null
          : responses[0].data.find((c) => c.catalogId === panel.queryId);
        if (!panel.isEmpty && (!loaded || !Array.isArray(loaded.Products)))
          throw Error();
        const initial = draft(loaded);
        setRow(loaded);
        setForm(initial);
        setBaseline(initial);
        setMembers(memberIds(loaded));
        setOriginalMembers(memberIds(loaded));
        setAvailable({
          products: responses[1].data,
          categories: responses[2].data,
        });
      })
      .catch(() => {
        if (current)
          setError("No se ha podido cargar el catálogo. Vuelve a intentarlo.");
      });
    return () => {
      current = false;
    };
  }, [panel.queryId, panel.isEmpty, attempt]);
  useEffect(() => {
    const warn = (event) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const change = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setSaved(false);
  };
  const save = async (event) => {
    event.preventDefault();
    if (lock.current || uncertain) return;
    if (!form.name.trim()) {
      editor.current.querySelector("#catalog-name")?.focus();
      setError("Escribe el nombre del catálogo.");
      return;
    }
    lock.current = true;
    setBusy(true);
    setError("");
    setSaved(false);
    let target = id;
    try {
      if (!target) {
        // A creation with an unknown outcome must never be automatically repeated.
        setUncertain(true);
        const response = await createCatalog({
          ...form,
          name: form.name.trim(),
          productIds: members,
        });
        if (response?.error !== false || !response.data?.catalogId)
          throw Error(
            "No se ha podido confirmar la creación. Vuelve al listado para comprobar si el catálogo se ha creado.",
          );
        target = response.data.catalogId;
        setId(target);
        setUncertain(false);
      } else {
        const response = await updateCatalog(target, {
          ...form,
          name: form.name.trim(),
        });
        if (response?.error !== false)
          throw Error(
            "No se han podido guardar los detalles. Tus cambios se conservan.",
          );
        if (!equalIds(members, originalMembers)) {
          const membership = await updateCatalogProducts(target, members);
          if (membership?.error !== false)
            throw Error(
              "Los detalles se han guardado, pero no se han confirmado los productos. Tus cambios se conservan; vuelve a guardar.",
            );
        }
      }
      const check = await getCatalogs();
      const confirmed =
        check?.error === false &&
        Array.isArray(check.data) &&
        check.data.find((c) => c.catalogId === target);
      if (
        !confirmed ||
        !Array.isArray(confirmed.Products) ||
        JSON.stringify(draft(confirmed)) !==
          JSON.stringify({ ...form, name: form.name.trim() }) ||
        !equalIds(memberIds(confirmed), members)
      )
        throw Error(
          "No se ha podido confirmar el guardado. Tus cambios se conservan; vuelve a comprobarlo.",
        );
      setRow(confirmed);
      setForm(draft(confirmed));
      setBaseline(draft(confirmed));
      setOriginalMembers(memberIds(confirmed));
      setSaved(true);
      dispatch(requestTableRefresh());
    } catch (e) {
      setError(
        !target
          ? "No se ha podido confirmar la creación. Vuelve al listado para comprobar si el catálogo se ha creado."
          : e.message ||
              "No se ha podido guardar el catálogo. Tus cambios se conservan.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const openPicker = (category) => {
    setPicker({ category });
    setPickerSearch("");
    setPicks([]);
  };
  const togglePick = (id) =>
    setPicks((list) =>
      list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
    );
  const needle = normalizeSearch(search.trim());
  const products = available?.products || [];
  const memberProducts = members.map(
    (productId) =>
      products.find((p) => p.productId === productId) || {
        productId,
        name: "Producto no disponible",
      },
  );
  const filtered = memberProducts.filter((p) =>
    normalizeSearch(`${p.name || ""} ${p.reference || ""}`).includes(needle),
  );
  const groups = (available?.categories || [])
    .map((c) => ({
      ...c,
      rows: filtered.filter((p) => p.categoryIds?.includes(c.categoryId)),
    }))
    .filter((c) => c.rows.length);
  const ungrouped = filtered.filter(
    (p) =>
      !p.categoryIds?.some((categoryId) =>
        available?.categories.some((c) => c.categoryId === categoryId),
      ),
  );
  if (ungrouped.length)
    groups.push({
      categoryId: "uncategorized",
      name: "Sin categoría",
      rows: ungrouped,
    });
  const pickerProducts = products.filter(
    (p) =>
      !members.includes(p.productId) &&
      (!picker?.category || p.categoryIds?.includes(picker.category)) &&
      normalizeSearch(`${p.name || ""} ${p.reference || ""}`).includes(
        normalizeSearch(pickerSearch.trim()),
      ),
  );
  return (
    <div
      ref={editor}
      className="catalogReferenceEditor"
      aria-busy={busy}
      onKeyDownCapture={(event) => {
        if (event.key === "Escape" && !picker && !discard) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
      }}
    >
      <form onSubmit={save}>
        <header className="catalogEditorHeading">
          <button
            className="catalogBack"
            type="button"
            disabled={busy}
            onClick={close}
          >
            <LuArrowLeft /> Catálogos
          </button>
          <div>
            <h2 id="admin-editor-title">
              {row?.name || (panel.isEmpty ? "Nuevo catálogo" : "Catálogo")}
            </h2>
            <div className="catalogHeadingActions">
              <span>Estado</span>
              <button
                type="button"
                role="switch"
                aria-label="Estado del catálogo"
                aria-checked={form?.isActive || false}
                className="catalogSwitch"
                disabled={!form || busy || uncertain}
                onClick={() => change("isActive", !form.isActive)}
              >
                <span />
                {form?.isActive ? "Activo" : "Inactivo"}
              </button>
              <button
                className="catalogPrimary"
                disabled={!form || busy || uncertain || (!dirty && !!id)}
              >
                {busy ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
          {dirty && <p className="catalogFeedback">Cambios sin guardar</p>}
          {saved && <p role="status">Cambios guardados</p>}
        </header>
        {error && (
          <div className="catalogError" role="alert">
            {error}
            {!available && (
              <button type="button" onClick={() => setAttempt((a) => a + 1)}>
                Reintentar
              </button>
            )}
          </div>
        )}
        {!available ? (
          !error && <p role="status">Cargando catálogo…</p>
        ) : (
          <>
            <section className="catalogEditorCard">
              <header>
                <h3>Detalles</h3>
                <p>
                  Cómo se identifica el catálogo en el panel y en la tienda.
                </p>
              </header>
              <fieldset
                disabled={busy || uncertain}
                className="catalogDetailsFields"
              >
                <AdminInput
                  id="catalog-name"
                  label="Nombre"
                  required
                  value={form.name}
                  description="Nombre completo"
                  onChange={(value) => change("name", value)}
                />
                <AdminInput
                  id="catalog-reference"
                  label="Referencia"
                  value={row?.reference || ""}
                  readOnly
                  placeholder="—"
                  description="Código interno del catálogo"
                />
                <AdminTextarea
                  id="catalog-description"
                  label="Descripción"
                  rows={5}
                  value={form.description}
                  onChange={(event) =>
                    change("description", event.target.value)
                  }
                  description="Uso interno: para qué se publica este catálogo."
                />
              </fieldset>
            </section>
            <section className="catalogEditorCard catalogProducts">
              <header>
                <div>
                  <h3>
                    Productos{" "}
                    <span className="catalogCount">
                      {members.length} productos
                    </span>
                  </h3>
                  <p>
                    Selecciona los productos que estarán disponibles en este
                    catálogo.
                  </p>
                </div>
                <div className="catalogProductTools">
                  <label className="catalogSearch">
                    <LuSearch />
                    <input
                      type="search"
                      aria-label="Buscar productos del catálogo"
                      placeholder="Buscar por nombre o referencia"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="catalogPrimary"
                    disabled={busy || uncertain}
                    onClick={() => openPicker(null)}
                  >
                    <LuPlus /> Agregar productos
                  </button>
                  <button
                    type="button"
                    disabled={busy || uncertain}
                    onClick={() => {
                      setSelecting(!selecting);
                      setSelected([]);
                    }}
                  >
                    {selecting ? "Cancelar selección" : "Seleccionar"}
                  </button>
                </div>
              </header>
              {selecting && (
                <div className="catalogSelection">
                  <span>{selected.length} seleccionados</span>
                  <button
                    type="button"
                    disabled={!selected.length || busy || uncertain}
                    onClick={() => {
                      setMembers((list) =>
                        list.filter((p) => !selected.includes(p)),
                      );
                      setSelected([]);
                      setSaved(false);
                    }}
                  >
                    Quitar del catálogo
                  </button>
                </div>
              )}
              {!groups.length && (
                <p className="catalogEmpty">
                  {members.length
                    ? "No hay productos que coincidan con la búsqueda."
                    : "Este catálogo todavía no tiene productos."}
                </p>
              )}
              {groups.map((group, index) => (
                <details
                  className="catalogProductGroup"
                  key={group.categoryId}
                  open={index === 0 || !!search || undefined}
                >
                  <summary>
                    <strong>{group.name}</strong>
                    <span>{group.rows.length} productos</span>
                    <LuChevronRight />
                  </summary>
                  <div>
                    {group.rows.map((p) => (
                      <div className="catalogProductRow" key={p.productId}>
                        {selecting && (
                          <input
                            type="checkbox"
                            aria-label={`Seleccionar ${p.name}`}
                            checked={selected.includes(p.productId)}
                            disabled={busy || uncertain}
                            onChange={() =>
                              setSelected((list) =>
                                list.includes(p.productId)
                                  ? list.filter((x) => x !== p.productId)
                                  : [...list, p.productId],
                              )
                            }
                          />
                        )}
                        <span className="catalogProductRef">
                          {p.reference || "—"}
                        </span>
                        <div className="catalogProductImage">
                          {p.images?.[0] ? (
                            <img src={p.images[0]} alt="" />
                          ) : (
                            <LuImageOff />
                          )}
                        </div>
                        <strong>{p.name}</strong>
                        <strong className="catalogPrice">
                          {productMoney(productPriceWithTax(p))}
                        </strong>
                        <span
                          className={`catalogProductState ${p.status === "PUBLISHED" ? "active" : ""}`}
                        >
                          {p.status === "PUBLISHED"
                            ? "Publicado"
                            : p.status === "DRAFT"
                              ? "Borrador"
                              : "Sin estado"}
                        </span>
                      </div>
                    ))}
                    <div className="catalogGroupAdd">
                      <button
                        type="button"
                        disabled={busy || uncertain}
                        onClick={() =>
                          openPicker(
                            group.categoryId === "uncategorized"
                              ? null
                              : group.categoryId,
                          )
                        }
                      >
                        <LuPlus /> Agregar productos
                      </button>
                    </div>
                  </div>
                </details>
              ))}
            </section>
          </>
        )}
      </form>
      {picker && (
        <EditorDialog
          title="Agregar productos"
          description="Selecciona los productos que quieres añadir al catálogo."
          onClose={() => setPicker(null)}
          footer={
            <>
              <button type="button" onClick={() => setPicker(null)}>
                Cancelar
              </button>
              <button
                className="catalogPrimary"
                type="button"
                disabled={!picks.length}
                onClick={() => {
                  setMembers((list) => [...new Set([...list, ...picks])]);
                  setSaved(false);
                  setPicker(null);
                }}
              >
                Añadir
              </button>
            </>
          }
        >
          <label className="catalogSearch">
            <LuSearch />
            <input
              type="search"
              aria-label="Buscar productos para añadir"
              placeholder="Buscar por nombre o referencia"
              value={pickerSearch}
              onChange={(event) => setPickerSearch(event.target.value)}
            />
          </label>
          <div className="referencePickerOptions">
            {pickerProducts.map((p) => (
              <label key={p.productId}>
                <input
                  aria-label={`Añadir ${p.name}`}
                  type="checkbox"
                  checked={picks.includes(p.productId)}
                  onChange={() => togglePick(p.productId)}
                />
                <span>
                  <strong>{p.name}</strong>
                  <small>
                    {p.reference || "Sin referencia"} ·{" "}
                    {productMoney(productPriceWithTax(p))}
                  </small>
                </span>
              </label>
            ))}
            {!pickerProducts.length && (
              <p>No hay productos disponibles que coincidan.</p>
            )}
          </div>
        </EditorDialog>
      )}
      {discard && (
        <EditorDialog
          title="Descartar cambios"
          description="Hay cambios sin guardar en este catálogo."
          onClose={() => setDiscard(false)}
          footer={
            <>
              <button type="button" onClick={() => setDiscard(false)}>
                Seguir editando
              </button>
              <button
                type="button"
                onClick={() =>
                  dispatch(
                    updateFloatPanel({
                      visible: false,
                      queryId: null,
                      isEmpty: true,
                    }),
                  )
                }
              >
                Descartar y volver
              </button>
            </>
          }
        />
      )}
    </div>
  );
};
